import React, { useEffect, useState } from 'react';
import { StatsCard } from 'common';
import { ENDPOINTS, QUICK_ENTRY_SELECTION_KEYS } from 'features/quick-entry/constants';
import { useQuickEntryContext } from 'features/quick-entry/context';
import { useShowAlert } from 'hooks';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import DataTable from 'UI/components/organisms/DataTable';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import { RowActions } from 'UI/constants/defaults';
import { CandidateEntity } from 'UI/constants/entityTypes';

import { CandidateQuickEntryDrawer } from '../CandidateQuickEntryDrawer';

import {
  COLUMNS,
  COPIES,
  ORDER_BY_OPTIONS,
  PERSISTENCE_KEY,
  QUICK_ENTRY_DRAFT_STATUS_LABEL
} from './CandidateQuickEntryTable.constants';

export const CandidateQuickEntryTable = () => {
  const { showAlert } = useShowAlert();
  const [uiState, setUiState] = useState({
    isDeleteDialogOpen: false,
    isDeleting: false,
    selectedCandidateQuickEntryId: null,
    quickEntryToDelete: null
  });

  const initialPreferences = getColumnPreferences(PERSISTENCE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS);

  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: COLUMNS,
    endpoint: `${ENDPOINTS.quickEntries}?typeId=${QUICK_ENTRY_SELECTION_KEYS.candidate}`,
    initialPreferences,
    key: PERSISTENCE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns: finalColumns, columnOrder, orderBy, direction } = columnPreferences;

  const { registerCandidateRefetch } = useQuickEntryContext();

  useEffect(() => registerCandidateRefetch(getData), [getData, registerCandidateRefetch]);

  const handleOpenDeleteDialog = ({ rowData }) => {
    if (!rowData) return;
    const item = extractObjectFromDataTable(COLUMNS, ['id', 'candidateName'], rowData);

    setUiState(prev => ({
      ...prev,
      isDeleteDialogOpen: true,
      quickEntryToDelete: item
    }));
  };

  const handleDeleteQuickEntry = async () => {
    try {
      const { id: quickEntryId } = uiState.quickEntryToDelete || {};
      if (!quickEntryId) return;

      setUiState(prev => ({ ...prev, isDeleting: true }));
      await SECONDARY_API_CLIENT.delete(ENDPOINTS.quickEntriesById.replace(':id', quickEntryId));

      showAlert({
        title: strings.formatString(COPIES.deleteAlert.success, {
          entity: CandidateEntity.singular
        }),
        autoHideDuration: 5000
      });

      setUiState(prev => ({ ...prev, isDeleting: false, isDeleteDialogOpen: false }));
      await getData();
    } catch (error) {
      setUiState(prev => ({ ...prev, isDeleting: false }));

      showAlert({
        title: strings.formatString(COPIES.deleteAlert.error, { entity: CandidateEntity.singular }),
        error
      });
    }
  };

  const handleEditQuickEntry = async ({ rowData }) => {
    if (!rowData) return;

    const { id: rowId } = extractObjectFromDataTable(COLUMNS, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      selectedCandidateQuickEntryId: rowId
    }));
  };

  const checkQuickEntryStatus = ({ rowData }) => {
    if (!rowData) return false;

    const item = extractObjectFromDataTable(COLUMNS, ['id', 'statusTitle'], rowData);
    return item.statusTitle === QUICK_ENTRY_DRAFT_STATUS_LABEL;
  };

  const actionsButton = buildRowActionsRendererDefinition({
    actions: [
      { name: RowActions.Edit, onClick: handleEditQuickEntry, enableWhen: checkQuickEntryStatus },
      {
        name: RowActions.Delete,
        onClick: handleOpenDeleteDialog,
        enableWhen: checkQuickEntryStatus
      }
    ],
    columns: COLUMNS
  });

  const columnsToRender = getColumnsToRender([...finalColumns, actionsButton]);

  const closeDrawer = () => {
    setUiState(prev => ({
      ...prev,
      selectedCandidateQuickEntryId: null
    }));
  };

  return (
    <StatsCard header={COPIES.title} paddingContent={0}>
      <DataTable
        columnOrder={columnOrder?.length > 0 ? columnOrder : undefined}
        columns={columnsToRender}
        components={{ TableToolbar: () => null }}
        count={count ?? 0}
        data={data}
        draggableColumns={{ enabled: true }}
        elevation={0}
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        searchText={keyword}
        selectToolbarPlacement="none"
        selectableRows="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={COPIES.emptyState.subtitle}
        title={COPIES.emptyState.title}
      />

      <When condition={uiState.isDeleteDialogOpen}>
        <DecisionDialog
          fetching={uiState.isDeleting}
          isOpened
          message={strings.formatString(COPIES.deleteDialog.message, {
            title: uiState.quickEntryToDelete?.candidateName
          })}
          onClose={() => setUiState(prev => ({ ...prev, isDeleteDialogOpen: false }))}
          onConfirm={handleDeleteQuickEntry}
          showSeverityIcon
          title={COPIES.deleteDialog.title}
          withButtons="YesNo"
        />
      </When>

      <When condition={!!uiState.selectedCandidateQuickEntryId}>
        <CandidateQuickEntryDrawer
          onClose={closeDrawer}
          quickEntryId={uiState.selectedCandidateQuickEntryId}
        />
      </When>
    </StatsCard>
  );
};
